import React, { useEffect, useMemo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@material-ui/core'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { SmartProfilePeriod, SmartProfileSummary } from '../../shared/types/flexiCharging'
import { fetchSession } from '../../redux/actions/sessions'

interface FlexiPlanDialogProps {
    open: boolean
    onClose: () => void
    session: any
}

export default function FlexiPlanDialog({
    open,
    onClose,
    session,
}: FlexiPlanDialogProps) {
    const dispatch = useDispatch()

    const sessionDetail = useSelector(
        (state: any) => state.sessions.sessionDetail
    )

    const schedule = session?.flexiChargingSchedule

    const summary: SmartProfileSummary | null =
        sessionDetail?.value?.smartProfile ?? null

    const loading = open && !sessionDetail?.executed

    useEffect(() => {
        if (open && session?.id) {
            dispatch(fetchSession(session.id))
        }
    }, [open, session?.id, dispatch])

    const visiblePeriods = useMemo(() => {
        if (!summary?.schedule) {
            return []
        }

        return summary.schedule.filter(
            (period: SmartProfilePeriod) =>
                period.from !== period.to
        )
    }, [summary])

    const getStatusLabel = (status: number | null | undefined) => {
        switch (status) {
            case 0:
                return 'SCHEDULED'
            case 1:
                return 'ACTIVE'
            case 2:
                return 'COMPLETED'
            case 3:
                return 'ABORTED'
            case 4:
                return 'REJECTED'
            default:
                return '-'
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Flexi Plan
            </DialogTitle>

            <DialogContent dividers>
                {loading && (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: 30,
                        }}
                    >
                        <CircularProgress />
                    </div>
                )}

                {!loading && (
                    <>
                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    Charging window
                                </Typography>

                                <Typography>
                                    {schedule?.from
                                        ? moment(schedule.from).format('LLL')
                                        : '-'}
                                    {' - '}
                                    {schedule?.to
                                        ? moment(schedule.to).format('LLL')
                                        : '-'}
                                </Typography>
                            </Grid>

                            <Grid item xs={3}>
                                <Typography variant="body2">
                                    Connector
                                </Typography>

                                <Typography>
                                    {summary?.connectorType ?? '-'}
                                </Typography>
                            </Grid>

                            <Grid item xs={3}>
                                <Typography variant="body2">
                                    Status
                                </Typography>

                                <Typography>
                                    {getStatusLabel(schedule?.status)}
                                </Typography>
                            </Grid>

                            <Grid item xs={3}>
                                <Typography variant="body2">
                                    Requested energy
                                </Typography>

                                <Typography>
                                    {schedule?.requestedKwh != null
                                        ? `${schedule.requestedKwh} kWh`
                                        : '-'}
                                </Typography>
                            </Grid>

                            <Grid item xs={3}>
                                <Typography variant="body2">
                                    Planned energy
                                </Typography>

                                <Typography>
                                    {summary?.totalPlannedEnergyKwh != null
                                        ? `${summary.totalPlannedEnergyKwh} kWh`
                                        : '-'}
                                </Typography>
                            </Grid>

                            <Grid item xs={3}>
                                <Typography variant="body2">
                                    Consumed energy
                                </Typography>

                                <Typography>
                                    {schedule?.consumedKwh != null
                                        ? `${schedule.consumedKwh} kWh`
                                        : '-'}
                                </Typography>
                            </Grid>

                            <Grid item xs={3}>
                                <Typography variant="body2">
                                    Overtime charging
                                </Typography>

                                <Typography>
                                    {schedule?.overtimeCharging
                                        ? 'Yes'
                                        : 'No'}
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    Ended at
                                </Typography>

                                <Typography>
                                    {schedule?.endedAt
                                        ? moment(schedule.endedAt).format('LLL')
                                        : '-'}
                                </Typography>
                            </Grid>
                        </Grid>

                        {visiblePeriods.length > 0 ? (
                            <Table
                                size="small"
                                style={{
                                    marginTop: 24,
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            Time
                                        </TableCell>

                                        <TableCell>
                                            Power
                                        </TableCell>

                                        <TableCell>
                                            Limit
                                        </TableCell>

                                        <TableCell>
                                            Energy
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {visiblePeriods.map(
                                        (period, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    {period.from}
                                                    {' - '}
                                                    {period.to}

                                                    {period.dateLabel
                                                        ? ` (${period.dateLabel})`
                                                        : ''}
                                                </TableCell>

                                                <TableCell>
                                                    {period.power}
                                                </TableCell>

                                                <TableCell>
                                                    {period.limit}
                                                </TableCell>

                                                <TableCell>
                                                    {period.energy}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography
                                align="center"
                                style={{
                                    marginTop: 24,
                                }}
                            >
                                Smart plan not available.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}